<template>
  <div class="summary-list-page">
    <header ref="summaryListFixedBar" class="summary-list-fixed-bar">
      <section class="search-wrap">
        <el-input
          v-model.trim="theme"
          class="search-input"
          placeholder="搜索标题..."
          clearable
          @keyup.enter.native="handleSearch"
        >
          <i slot="prefix" class="el-input__icon el-icon-search"></i>
        </el-input>
      </section>

      <section class="tags-strip">
        <div class="tags-strip__scroll">
          <button
            v-for="item in menuList"
            :key="item.parentLabelCode === '' ? 'all' : item.parentLabelCode"
            class="tag-btn"
            :class="{ active: selectedParentLabelCode === item.parentLabelCode }"
            @click="handleParentLabelClick(item)"
          >
            {{ item.parentLabel }}
          </button>
        </div>
        <button type="button" class="tags-strip__filter" aria-label="筛选" @click="openFilterPanel">
          <i class="el-icon-s-operation"></i>
        </button>
      </section>
    </header>

    <div
      class="summary-list-fixed-spacer"
      :style="{ height: fixedBarHeight + 'px' }"
      aria-hidden="true"
    />

    <div
      v-show="filterPanelVisible"
      class="summary-filter-mask"
      @click="closeFilterPanel"
    />

    <transition name="summary-filter-drawer">
      <div v-show="filterPanelVisible" class="summary-filter-panel">
        <div class="summary-filter-panel__header">
          <span>筛选条件</span>
          <button type="button" class="summary-filter-panel__close" aria-label="关闭" @click="closeFilterPanel">
            <i class="el-icon-close"></i>
          </button>
        </div>
        <div class="summary-filter-panel__body">
          <div class="summary-filter-section">
            <div class="summary-filter-section__title">创建时间</div>
            <!-- 双 date 单月历：避免 daterange 双面板被 CSS 折腾；起止各选一个面板，前后年月均可翻 -->
            <div
              class="summary-filter-date-row"
              :class="{ 'summary-filter-date-row--filled': hasDraftDateRange }"
            >
              <el-date-picker
                ref="filterDraftDateStart"
                v-model="draftStartDate"
                type="date"
                size="small"
                clearable
                value-format="yyyy-MM-dd"
                placeholder="开始日期"
                :editable="false"
                :append-to-body="true"
                popper-class="summary-filter-date-popper"
                class="summary-filter-date-input"
                @visible-change="(v) => onFilterDatePickerVisible(v, 'start')"
              />
              <span class="summary-filter-date-sep">至</span>
              <el-date-picker
                ref="filterDraftDateEnd"
                v-model="draftEndDate"
                type="date"
                size="small"
                clearable
                value-format="yyyy-MM-dd"
                placeholder="结束日期"
                :editable="false"
                :append-to-body="true"
                popper-class="summary-filter-date-popper"
                class="summary-filter-date-input"
                @visible-change="(v) => onFilterDatePickerVisible(v, 'end')"
              />
            </div>
          </div>
          <div class="summary-filter-section">
            <div class="summary-filter-label-row">
              <span class="summary-filter-section__title summary-filter-section__title--row">内容标签</span>
              <button
                v-if="filterTagExpandVisible"
                type="button"
                class="summary-filter-expand-btn"
                @click="filterTagsExpanded = !filterTagsExpanded"
              >
                <span>{{ filterTagsExpanded ? '收起' : '展开' }}</span>
                <i :class="filterTagsExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
              </button>
            </div>
            <div class="summary-filter-tag-bubbles">
              <button
                v-for="it in filterTagBubblesForView"
                :key="it.code === '' ? 'all' : it.code"
                type="button"
                class="summary-filter-bubble"
                :class="{ 'is-active': draftParentLabelCode === it.code }"
                @click="draftParentLabelCode = it.code"
              >
                {{ it.label }}
              </button>
            </div>
          </div>
        </div>
        <div class="summary-filter-panel__footer">
          <button type="button" class="summary-filter-footer-pill" @click="resetFilterDraft">清空</button>
          <button type="button" class="summary-filter-footer-pill summary-filter-footer-pill--primary" @click="confirmFilter">
            确定
          </button>
        </div>
      </div>
    </transition>

    <section class="list-wrap">
      <article
        v-for="item in textList"
        :key="item._uniqueId"
        class="summary-card"
        @click="goDetail(item)"
      >
        <div class="card-top">
          <span class="badge">{{ item.parentLabel || '通用' }}</span>
          <span class="date">{{ formatDate(item.createTime || item.createdAt || item.create_date) }}</span>
        </div>
        <h3>{{ item.theme || '未命名总结' }}</h3>
        <div class="card-content">
          <div
            v-for="(line, i) in linesForCardPreview(item.summarized_text || item.summarizedText || item.summary || item.content, 80)"
            :key="`${item._uniqueId}-${i}`"
            class="summary-line"
          >
            {{ line }}
          </div>
        </div>
        <div class="card-foot">
          <span><i class="el-icon-collection-tag"></i> 内容标签</span>
          <span
            v-for="(tag, idx) in getLabels(item)"
            :key="`${item._uniqueId}-tag-${idx}`"
            class="sub-label"
          >
            #{{ tag }}
          </span>
        </div>
      </article>

      <div v-if="loading" class="list-status list-status--loading">
        <i class="el-icon-loading"></i>
        <span>加载中...</span>
      </div>
      <div v-else-if="!textList.length" class="list-status empty-text">暂无数据</div>
      <div v-else-if="!hasMore" class="list-status no-more-text">没有更多了</div>
      <div v-if="loadingMore" class="list-status list-status--loading list-status--more">
        <i class="el-icon-loading"></i>
        <span>正在加载更多...</span>
      </div>
      <div ref="loadMoreTrigger" class="load-trigger"></div>
    </section>

    <button v-if="showBackTop" class="back-top-btn" @click="scrollToTop">
      <i class="el-icon-arrow-up"></i>
    </button>
  </div>
</template>

<script>
import axios from 'axios'
import { getApiErrorMessage } from '@/utils/apiErrorMessage'
import { fetchMenuList } from '@/api/homeMenuList'
import { splitNumberedLinesForPreview } from '@/utils/splitNumberedText'

export default {
  name: 'SummaryListPage',
  data() {
    return {
      userId: null,
      theme: '',
      menuList: [{ parentLabelCode: '', parentLabel: '全部' }],
      selectedParentLabelCode: '',
      textList: [],
      pageNum: 1,
      pageSize: 20,
      hasMore: true,
      loading: false,
      loadingMore: false,
      isRequesting: false,
      observer: null,
      /** fixed 顶栏占位高度，由 DOM 测量，避免列表被遮挡 */
      fixedBarHeight: 128,
      showBackTop: false,
      scrollHandler: null,
      lastScrollTop: 0,
      filterPanelVisible: false,
      draftStartDate: null,
      draftEndDate: null,
      draftParentLabelCode: '',
      appliedStartDate: '',
      appliedEndDate: '',
      /** 内容标签气泡：展开后显示全部 */
      filterTagsExpanded: false
    }
  },
  computed: {
    /** 半屏筛选里「内容标签」单选：不含「全部」那一项 */
    menuListForFilter() {
      return this.menuList.filter((x) => x.parentLabelCode !== '')
    },
    /** 不限 + 各标签，用于气泡网格 */
    filterTagBubbleListFull() {
      return [
        { code: '', label: '不限' },
        ...this.menuListForFilter.map((t) => ({ code: t.parentLabelCode, label: t.parentLabel }))
      ]
    },
    /** 超过 8 个（两行×每行四个）时显示展开入口 */
    filterTagExpandVisible() {
      return this.filterTagBubbleListFull.length > 8
    },
    /** 折叠时只展示前 8 个 */
    filterTagBubblesForView() {
      const full = this.filterTagBubbleListFull
      if (this.filterTagsExpanded || full.length <= 8) return full
      return full.slice(0, 8)
    },
    /** 起止日期都已选 */
    hasDraftDateRange() {
      return !!(
        this.normalizeDraftRangePart(this.draftStartDate) &&
        this.normalizeDraftRangePart(this.draftEndDate)
      )
    }
  },
  async mounted() {
    this.userId = localStorage.getItem('userId') || null
    if (!this.userId) {
      this.$message.error('请先登录')
      return
    }
    await this.loadMenus()
    await this.fetchTextList(false)
    this.initObserver()
    this.initScrollListener()
    this.setupFixedBarMeasure()
    this.$bus.$on('refresh-summary-list', this.refreshAndScrollToTop)
    this._onSummaryFilterResize = () => {
      if (this.filterPanelVisible) this.syncFilterDatePopperWidth('start')
    }
    window.addEventListener('resize', this._onSummaryFilterResize)
  },
  beforeDestroy() {
    document.documentElement.style.removeProperty('--summary-filter-date-input-width')
    if (this._onSummaryFilterResize) {
      window.removeEventListener('resize', this._onSummaryFilterResize)
      this._onSummaryFilterResize = null
    }
    if (this.observer) this.observer.disconnect()
    if (this.scrollHandler) {
      const contentArea = document.querySelector('.content-area')
      if (contentArea) contentArea.removeEventListener('scroll', this.scrollHandler)
      this.scrollHandler = null
    }
    if (this._fixedBarRO) {
      this._fixedBarRO.disconnect()
      this._fixedBarRO = null
    }
    this.$bus.$off('refresh-summary-list', this.refreshAndScrollToTop)
  },
  activated() {
    this.$nextTick(() => {
      this.measureFixedBarHeight()
      this.restoreScrollPositionWithRetry()
    })
  },
  deactivated() {
    this.cacheScrollPosition(false)
  },
  watch: {
    '$route.query.restoreScroll'() {
      this.restoreScrollPositionWithRetry()
    }
  },
  beforeRouteLeave(to, from, next) {
    this.cacheScrollPosition()
    next()
  },
  methods: {
    /**
     * 一级标签接口（参考 HomePage）
     * GET /api/app/parentLabel/list
     * 入参：无
     * 返回：[{ parentLabelCode, parentLabel }, ...]
     */
    async loadMenus() {
      const list = await fetchMenuList()
      this.menuList = [{ parentLabelCode: '', parentLabel: '全部' }, ...list]
      this.$nextTick(() => this.measureFixedBarHeight())
    },
    measureFixedBarHeight() {
      const el = this.$refs.summaryListFixedBar
      if (!el) return
      const h = Math.ceil(el.getBoundingClientRect().height)
      if (h > 0) this.fixedBarHeight = h
    },
    setupFixedBarMeasure() {
      this.$nextTick(() => {
        this.measureFixedBarHeight()
        const el = this.$refs.summaryListFixedBar
        if (!el || typeof ResizeObserver === 'undefined') return
        if (this._fixedBarRO) this._fixedBarRO.disconnect()
        this._fixedBarRO = new ResizeObserver(() => this.measureFixedBarHeight())
        this._fixedBarRO.observe(el)
      })
    },
    handleSearch() {
      this.fetchTextList(false)
    },
    initDraftFromApplied() {
      if (this.appliedStartDate && this.appliedEndDate) {
        this.draftStartDate = String(this.appliedStartDate).slice(0, 10)
        this.draftEndDate = String(this.appliedEndDate).slice(0, 10)
      } else {
        this.draftStartDate = null
        this.draftEndDate = null
      }
      this.draftParentLabelCode = this.selectedParentLabelCode || ''
    },
    openFilterPanel() {
      this.initDraftFromApplied()
      const list = this.filterTagBubbleListFull
      const idx = list.findIndex((x) => x.code === this.draftParentLabelCode)
      this.filterTagsExpanded = list.length > 8 && idx > 7
      this.filterPanelVisible = true
      this.$nextTick(() => {
        this.syncFilterDatePopperWidth('start')
      })
    },
    /**
     * 下拉宽度：参考当前输入框，但不小于 320px，保证单月历格子能完整展示；不超过视口。
     */
    syncFilterDatePopperWidth(which) {
      const ref =
        which === 'end' ? this.$refs.filterDraftDateEnd : this.$refs.filterDraftDateStart
      const root = ref && ref.$el
      if (!root) return
      const raw = root.offsetWidth || 0
      const vw = typeof window !== 'undefined' ? window.innerWidth : 800
      const minW = 320
      const maxW = Math.max(vw - 32, minW)
      const w = Math.min(Math.max(raw, minW), maxW)
      document.documentElement.style.setProperty('--summary-filter-date-input-width', `${w}px`)
    },
    onFilterDatePickerVisible(visible, which) {
      if (visible) {
        this.$nextTick(() => this.syncFilterDatePopperWidth(which || 'start'))
      }
    },
    closeFilterPanel() {
      this.filterPanelVisible = false
    },
    resetFilterDraft() {
      this.draftStartDate = null
      this.draftEndDate = null
      this.draftParentLabelCode = ''
    },
    /**
     * 将日期区间的单段值统一为 yyyy-MM-dd（兼容 string / Date / 时间戳）
     * value-format 在部分交互下仍可能得到 Date，不能假定是字符串。
     */
    normalizeDraftRangePart(val) {
      if (val == null || val === '') return ''
      if (val instanceof Date) {
        if (Number.isNaN(val.getTime())) return ''
        const y = val.getFullYear()
        const m = String(val.getMonth() + 1).padStart(2, '0')
        const d = String(val.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
      }
      if (typeof val === 'number' && Number.isFinite(val)) {
        const dt = new Date(val)
        if (Number.isNaN(dt.getTime())) return ''
        const y = dt.getFullYear()
        const m = String(dt.getMonth() + 1).padStart(2, '0')
        const d = String(dt.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
      }
      const s = String(val).trim()
      const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
      if (!m) return ''
      const y = parseInt(m[1], 10)
      const mo = parseInt(m[2], 10)
      const day = parseInt(m[3], 10)
      const dt = new Date(y, mo - 1, day)
      if (
        Number.isNaN(dt.getTime()) ||
        dt.getFullYear() !== y ||
        dt.getMonth() !== mo - 1 ||
        dt.getDate() !== day
      ) {
        return ''
      }
      return `${y}-${String(mo).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    },
    confirmFilter() {
      const a = this.normalizeDraftRangePart(this.draftStartDate)
      const b = this.normalizeDraftRangePart(this.draftEndDate)
      const hasAny = !!(a || b)
      const hasRange = !!(a && b)

      if (!hasAny) {
        this.appliedStartDate = ''
        this.appliedEndDate = ''
        this.selectedParentLabelCode = this.draftParentLabelCode
        this.filterPanelVisible = false
        this.fetchTextList(false)
        return
      }

      if (!hasRange) {
        this.$message.warning('请同时选择开始日期与结束日期')
        return
      }

      const y0 = parseInt(a.slice(0, 4), 10)
      const m0 = parseInt(a.slice(5, 7), 10) - 1
      const d0n = parseInt(a.slice(8, 10), 10)
      const y1 = parseInt(b.slice(0, 4), 10)
      const m1 = parseInt(b.slice(5, 7), 10) - 1
      const d1n = parseInt(b.slice(8, 10), 10)
      const d0 = new Date(y0, m0, d0n)
      const d1 = new Date(y1, m1, d1n)
      if (Number.isNaN(d0.getTime()) || Number.isNaN(d1.getTime())) {
        this.$message.warning('日期无效')
        return
      }
      const days = Math.floor((d1 - d0) / 86400000) + 1
      if (days < 1) {
        this.$message.warning('结束日期不能早于开始日期')
        return
      }
      if (days > 7) {
        this.$message.error('时间筛选跨度最多 7 天（按自然日），请缩小范围后重试')
        return
      }
      this.appliedStartDate = `${a} 00:00:00`
      this.appliedEndDate = `${b} 23:59:59`
      this.selectedParentLabelCode = this.draftParentLabelCode
      this.filterPanelVisible = false
      this.fetchTextList(false)
    },
    handleParentLabelClick(item) {
      if (!item || this.selectedParentLabelCode === item.parentLabelCode) return
      this.selectedParentLabelCode = item.parentLabelCode
      this.appliedStartDate = ''
      this.appliedEndDate = ''
      this.fetchTextList(false)
    },
    linesForCardPreview(text, maxTotal) {
      return splitNumberedLinesForPreview(text, maxTotal)
    },
    getLabels(item) {
      const raw =
        item?.labels ??
        item?.label ??
        item?.tags ??
        item?.tagList ??
        item?.subLabels ??
        []
      if (Array.isArray(raw)) {
        return raw.map((x) => String(x || '').trim()).filter(Boolean)
      }
      if (typeof raw === 'string') {
        const text = raw.trim()
        if (!text) return []
        // 兼容后端把标签返回成 JSON 字符串，如 '["A","B"]'
        if (text.startsWith('[') && text.endsWith(']')) {
          try {
            const arr = JSON.parse(text)
            if (Array.isArray(arr)) {
              return arr.map((x) => String(x || '').trim()).filter(Boolean)
            }
          } catch (e) {
            // ignore and fallback to split
          }
        }
        return raw
          .split(/[，,、\s]+/)
          .map((x) => x.trim())
          .filter(Boolean)
      }
      return []
    },
    formatDate(raw) {
      if (!raw) return ''
      const d = new Date(raw)
      if (Number.isNaN(d.getTime())) return String(raw).slice(0, 10)
      return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`
    },
    normalizeListData(payload) {
      if (Array.isArray(payload)) return payload
      if (Array.isArray(payload?.data)) return payload.data
      if (Array.isArray(payload?.list)) return payload.list
      if (Array.isArray(payload?.records)) return payload.records
      return []
    },
    /** 已应用起止（自然日）包含天数；无完整区间时为 0 */
    getAppliedRangeInclusiveDays() {
      if (!this.appliedStartDate || !this.appliedEndDate) return 0
      const a = this.normalizeDraftRangePart(this.appliedStartDate) || String(this.appliedStartDate).slice(0, 10)
      const b = this.normalizeDraftRangePart(this.appliedEndDate) || String(this.appliedEndDate).slice(0, 10)
      if (!a || !b) return 0
      const y0 = parseInt(a.slice(0, 4), 10)
      const m0 = parseInt(a.slice(5, 7), 10) - 1
      const d0n = parseInt(a.slice(8, 10), 10)
      const y1 = parseInt(b.slice(0, 4), 10)
      const m1 = parseInt(b.slice(5, 7), 10) - 1
      const d1n = parseInt(b.slice(8, 10), 10)
      const d0 = new Date(y0, m0, d0n)
      const d1 = new Date(y1, m1, d1n)
      if (Number.isNaN(d0.getTime()) || Number.isNaN(d1.getTime())) return 0
      return Math.floor((d1 - d0) / 86400000) + 1
    },
    /**
     * 列表接口（参考 HomePage）
     * POST /api/app/core/text-summary/list
     * 入参：
     * {
     *   userId: string|number,
     *   theme: string,
     *   parentLabelCode: string,
     *   pageNum: number,
     *   pageSize: number
     * }
     * 返回兼容：
     * - 直接数组
     * - { data: [] } / { list: [] } / { records: [], total: number }
     */
    async fetchTextList(isLoadMore) {
      if (!this.userId || this.isRequesting) return
      if (isLoadMore && (!this.hasMore || this.loadingMore)) return

      if (this.getAppliedRangeInclusiveDays() > 7) {
        this.$message.error('时间筛选跨度最多 7 天（按自然日），请缩小范围后重试')
        return
      }

      if (isLoadMore) {
        this.loadingMore = true
      } else {
        this.loading = true
        this.pageNum = 1
        this.hasMore = true
        this.textList = []
      }

      this.isRequesting = true
      const currentPage = this.pageNum
      try {
        const requestData = {
          theme: this.theme || '',
          parentLabelCode: this.selectedParentLabelCode || '',
          pageNum: currentPage,
          pageSize: this.pageSize
        }
        if (this.appliedStartDate) requestData.startDate = this.appliedStartDate
        if (this.appliedEndDate) requestData.endDate = this.appliedEndDate
        const response = await axios.post('/api/app/core/text-summary/list', requestData)
        const data = response.data
        const list = this.normalizeListData(data)

        const mapped = list.map((item) => ({
          ...item,
          summarized_text: item.summarized_text || item.summarizedText || item.content || '',
          _uniqueId: String(item.id ?? item._uniqueId ?? `${currentPage}-${Math.random()}`)
        }))

        this.textList = isLoadMore ? [...this.textList, ...mapped] : mapped
        this.pageNum = currentPage + 1
        this.hasMore = mapped.length >= this.pageSize
        if (typeof data?.total === 'number') {
          this.hasMore = data.total > currentPage * this.pageSize
        }
      } catch (error) {
        this.$message.error(getApiErrorMessage(error, '查询失败'))
      } finally {
        this.loading = false
        this.loadingMore = false
        this.isRequesting = false
      }
    },
    initObserver() {
      if (!window.IntersectionObserver) return
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.hasMore && !this.isRequesting) {
            this.fetchTextList(true)
          }
        })
      }, { root: null, rootMargin: '600px', threshold: 0.01 })

      this.$nextTick(() => {
        if (this.$refs.loadMoreTrigger) this.observer.observe(this.$refs.loadMoreTrigger)
      })
    },
    initScrollListener() {
      const contentArea = document.querySelector('.content-area')
      if (!contentArea) return
      const onScroll = () => {
        this.lastScrollTop = contentArea.scrollTop
        this.showBackTop = contentArea.scrollTop > 240
      }
      contentArea.addEventListener('scroll', onScroll)
      this.scrollHandler = onScroll
      onScroll()
    },
    cacheScrollPosition(useDom = true) {
      const contentArea = useDom ? document.querySelector('.content-area') : null
      const top = contentArea ? contentArea.scrollTop : this.lastScrollTop
      this.lastScrollTop = top || 0
      sessionStorage.setItem('summary-list-scroll-top', String(this.lastScrollTop))
    },
    restoreScrollPosition() {
      const contentArea = document.querySelector('.content-area')
      if (!contentArea || typeof contentArea.scrollTo !== 'function') return
      const cached = Number(sessionStorage.getItem('summary-list-scroll-top') || this.lastScrollTop || 0)
      if (!Number.isFinite(cached) || cached <= 0) return
      contentArea.scrollTop = cached
      contentArea.scrollTo({ top: cached, behavior: 'auto' })
      this.showBackTop = cached > 240
    },
    restoreScrollPositionWithRetry() {
      // 详情页会把容器滚到顶部，这里在激活后多次恢复，避免被后续渲染覆盖
      this.$nextTick(() => {
        this.restoreScrollPosition()
        requestAnimationFrame(() => this.restoreScrollPosition())
        setTimeout(() => this.restoreScrollPosition(), 60)
        setTimeout(() => this.restoreScrollPosition(), 180)
      })
    },
    scrollToTop() {
      const contentArea = document.querySelector('.content-area')
      if (contentArea && typeof contentArea.scrollTo === 'function') {
        contentArea.scrollTo({ top: 0, behavior: 'smooth' })
      } else if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    async refreshAndScrollToTop() {
      this.scrollToTop()
      this.lastScrollTop = 0
      sessionStorage.setItem('summary-list-scroll-top', '0')
      await this.fetchTextList(false)
    },
    goDetail(item) {
      this.cacheScrollPosition()
      const labels = this.getLabels(item)
      const payload = {
        sourceType: 'text',
        createdDate: item.createDate || item.createdDate || item.createTime || item.createdAt || item.create_date || '',
        createdAt: item.createDate || item.createdDate || item.createTime || item.createdAt || item.create_date || Date.now(),
        result: {
          idEzTextSummary: item.id || '',
          theme: item.theme || '',
          label: labels,
          summary: item.summarized_text || '',
          noteContent: item.noteContent || '',
          parentLabel: item.parentLabel || '',
          parentLabelCode: item.parentLabelCode || ''
        }
      }
      sessionStorage.setItem('summit-detail-payload', JSON.stringify(payload))
      const rid = item.id !== undefined && item.id !== null ? String(item.id).trim() : ''
      this.$router.push({
        path: '/summary-list/detail',
        query: {
          from: 'summary-list',
          ...(rid ? { id: rid } : {})
        }
      })
    }
  }
}
</script>

<style scoped>
.summary-list-page { min-height: 100%; background: #fff; padding: 0 16px 80px; }
/* 相对视口固定，避免 sticky 在 overflow 容器内跟手微动；占位块撑开列表起点 */
.summary-list-fixed-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 40;
  box-sizing: border-box;
  padding: calc(20px + env(safe-area-inset-top, 0px)) 16px 12px;
  background: #fff;
  border-bottom: 1px solid rgba(7, 29, 73, 0.08);
  transform: translateZ(0);
  backface-visibility: hidden;
}
.summary-list-fixed-bar .tags-strip {
  margin-bottom: 0;
}
.summary-list-fixed-spacer {
  width: 100%;
  flex-shrink: 0;
  pointer-events: none;
}
.list-wrap { width: 100%; box-sizing: border-box; }
.page-header h1 { margin: 0 0 14px; font-size: 42px; color: #071d49; }
.search-wrap { margin-bottom: 14px; }
.search-input /deep/ .el-input__inner { height: 44px; border-radius: 14px; background: #f5f9ff; border: none; }
.tags-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 2px 0 8px;
}
.tags-strip__scroll {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 2px;
  -webkit-overflow-scrolling: touch;
}
.tags-strip__scroll::-webkit-scrollbar { display: none; }
.tags-strip__filter {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 12px;
  background: #f5f9ff;
  color: #5d6f94;
  font-size: 20px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(66, 83, 255, 0.12);
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}
.tags-strip__filter:focus {
  outline: none;
}
.tags-strip__filter:focus-visible {
  outline: 2px solid rgba(66, 83, 255, 0.55);
  outline-offset: 2px;
}
.tags-strip__filter:active {
  background: #eef1ff;
}
.tags-strip__filter:hover {
  color: #4253ff;
  background: #eef1ff;
}
.tag-btn { border: none; background: #f5f9ff; color: #2c4774; border-radius: 18px; padding: 7px 16px; white-space: nowrap; font-size: 14px; flex-shrink: 0; }
.tag-btn.active { background: #4253ff; color: #fff; }
.summary-filter-mask {
  position: fixed;
  inset: 0;
  z-index: 2100;
  background: rgba(15, 28, 68, 0.38);
}
.summary-filter-panel {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2101;
  height: 50vh;
  min-height: 300px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 0 0 18px 18px;
  box-shadow: 0 12px 40px rgba(10, 28, 68, 0.18);
}
.summary-filter-drawer-enter-active,
.summary-filter-drawer-leave-active {
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}
.summary-filter-drawer-enter,
.summary-filter-drawer-leave-to {
  transform: translateY(-100%);
}
.summary-filter-panel__header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
  font-size: 17px;
  font-weight: 600;
  color: #16294f;
  border-bottom: 1px solid #f0f2f5;
}
.summary-filter-panel__close {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #909399;
  font-size: 18px;
  cursor: pointer;
}
.summary-filter-panel__close:hover {
  background: #f5f7fa;
  color: #4253ff;
}
.summary-filter-panel__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 16px 8px;
}
.summary-filter-section { margin-bottom: 18px; }
.summary-filter-section__title {
  font-size: 13px;
  font-weight: 600;
  color: #586a8f;
  margin-bottom: 10px;
}
.summary-filter-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}
.summary-filter-section__title--row {
  margin-bottom: 0;
}
.summary-filter-expand-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  padding: 4px 2px;
  background: transparent;
  color: #4253ff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.summary-filter-expand-btn:hover {
  color: #2f47ff;
}
.summary-filter-expand-btn i {
  font-size: 12px;
}
.summary-filter-date-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.summary-filter-date-sep {
  color: #97a5c2;
  font-size: 14px;
  flex-shrink: 0;
}
.summary-filter-date-input {
  flex: 1;
  min-width: 0;
  width: 100%;
}
.summary-filter-date-input /deep/ .el-date-editor.el-input {
  width: 100%;
}
.summary-filter-date-input /deep/ .el-input__inner {
  width: 100%;
  box-sizing: border-box;
  min-height: 44px;
  /* 勿用对称小 padding，否则会与左侧日历 icon、右侧清除 icon 重叠 */
  padding: 8px 36px 8px 34px;
  border-radius: 14px;
  border: 1px solid #e2e8f5;
  background: #f5f9ff;
  box-shadow: inset 0 1px 2px rgba(66, 83, 255, 0.04);
  font-size: 14px;
  color: #2c4774;
}
.summary-filter-date-input /deep/ .el-input__inner:hover {
  border-color: #c8d4f5;
}
.summary-filter-date-input /deep/ .el-input.is-active .el-input__inner,
.summary-filter-date-input /deep/ .el-input__inner:focus {
  border-color: #4253ff;
  box-shadow: 0 0 0 2px rgba(66, 83, 255, 0.12);
}
.summary-filter-date-input /deep/ .el-input__prefix,
.summary-filter-date-input /deep/ .el-input__suffix {
  color: #7a8eb5;
  display: flex;
  align-items: center;
}
.summary-filter-date-row--filled /deep/ .el-input__suffix .el-icon-close,
.summary-filter-date-row--filled /deep/ .el-input__suffix .el-icon-circle-close {
  display: inline-block !important;
  opacity: 1 !important;
  visibility: visible !important;
}
.summary-filter-tag-bubbles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  padding: 4px 0 2px;
}
.summary-filter-bubble {
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #dfe8ff;
  background: #fafcff;
  color: #2c4774;
  border-radius: 999px;
  padding: 8px 10px;
  font-size: 13px;
  line-height: 1.25;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.summary-filter-bubble:hover {
  border-color: #c8d4f5;
  background: #f5f9ff;
}
.summary-filter-bubble.is-active {
  border-color: #4253ff;
  background: #4253ff;
  color: #ffffff;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(66, 83, 255, 0.28);
}
.summary-filter-bubble.is-active:hover {
  border-color: #3648e6;
  background: #3648e6;
  color: #ffffff;
}
.summary-filter-panel__footer {
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 12px;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #f0f2f5;
  background: #fafcff;
  box-sizing: border-box;
}
.summary-filter-footer-pill {
  flex: 1 1 0;
  min-width: 0;
  border: 1px solid #dfe8ff;
  background: #fafcff;
  color: #2c4774;
  border-radius: 999px;
  padding: 10px 16px;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.2;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}
.summary-filter-footer-pill:hover {
  border-color: #c8d4f5;
  background: #f5f9ff;
}
.summary-filter-footer-pill--primary {
  border-color: #4253ff;
  background: #4253ff;
  color: #ffffff;
  font-weight: 600;
  box-shadow: 0 2px 10px rgba(66, 83, 255, 0.35);
}
.summary-filter-footer-pill--primary:hover {
  border-color: #3648e6;
  background: #3648e6;
  color: #ffffff;
}
.summary-card { width: 100%; box-sizing: border-box; border: 1px solid #eef3ff; border-radius: 16px; padding: 14px; margin: 0 0 12px; box-shadow: 0 2px 8px rgba(66, 83, 255, 0.05); }
.card-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.badge { display: inline-flex; align-items: center; height: 22px; border-radius: 8px; background: #4253ff; color: #ffffff; padding: 0 8px; font-size: 12px; }
.date { color: #95a2bf; font-size: 12px; }
.summary-card h3 { margin: 0 0 8px; color: #2f47ff; font-size: 18px; }
.card-content { color: #4f6188; font-size: 16px; line-height: 1.5; min-height: 44px; }
.summary-line + .summary-line { margin-top: 6px; }
.sub-label { display: inline-flex; align-items: center; height: 20px; border-radius: 999px; background: #f5f8ff; color: #5e73a8; font-size: 12px; padding: 0 8px; }
.card-foot { margin-top: 12px; border-top: 1px solid #f0f3fa; padding-top: 10px; color: #8d9bb8; font-size: 13px; display: flex; flex-wrap: wrap; gap: 8px; }
.list-status { text-align: center; color: #98a5c0; padding: 12px 0; font-size: 13px; }
.list-status--loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #5e73a8;
}
.list-status--loading .el-icon-loading {
  font-size: 18px;
  animation: rotating 1.2s linear infinite;
}
.list-status--more {
  padding: 16px 0 8px;
}
@keyframes rotating {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.load-trigger { height: 1px; }
.back-top-btn {
  position: fixed;
  right: 16px;
  bottom: 86px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: #4253ff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 16px rgba(66, 83, 255, 0.28);
  z-index: 120;
}
.back-top-btn i { font-size: 18px; }
@media (max-width: 768px) {
  .page-header h1 { font-size: 28px; }
  .summary-card h3 { font-size: 16px; }
  .card-content { font-size: 15px; }
  .back-top-btn {
    right: 12px;
    bottom: 80px;
    width: 36px;
    height: 36px;
  }
}
</style>

<!-- 单月历 type=date；宽度与日期行一致 -->
<style>
.summary-filter-date-popper {
  z-index: 3200 !important;
  border-radius: 14px;
  border: 1px solid #e8ecf5;
  box-shadow: 0 12px 36px rgba(10, 28, 68, 0.14);
  box-sizing: border-box;
  min-width: 320px !important;
  width: var(--summary-filter-date-input-width, 320px) !important;
  max-width: calc(100vw - 32px) !important;
}
.summary-filter-date-popper.el-picker-panel {
  line-height: 1.3;
}
.summary-filter-date-popper .el-picker-panel__body {
  border-radius: 0 0 12px 12px;
}
.summary-filter-date-popper .el-date-picker__header {
  margin: 0 8px;
}
.summary-filter-date-popper .el-date-picker__header-label,
.summary-filter-date-popper .el-date-picker__header div {
  font-size: 13px;
}
.summary-filter-date-popper .el-date-table {
  font-size: 12px;
}
.summary-filter-date-popper .el-date-table th,
.summary-filter-date-popper .el-date-table td {
  padding: 4px 2px;
}
.summary-filter-date-popper .el-date-table td .cell {
  height: 30px;
  line-height: 30px;
  min-width: 1.8em;
}
.summary-filter-date-popper .el-picker-panel__footer {
  padding: 6px 8px;
  font-size: 12px;
}

/* 日期/筛选层约 2100~3200；Element 默认 $message 约 2000+，会被挡住，统一抬高提示层 */
.el-message,
.el-notification,
.el-tooltip__popper {
  z-index: 5000 !important;
}
</style>
